import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, 
  Search,
  Filter,
  User,
  Settings,
  Shield,
  Clock
} from 'lucide-react';

export default function ActivityLog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const activityLogs = [
    {
      id: 1,
      type: 'user',
      action: 'User login',
      user: 'admin@consistency.app',
      timestamp: '2025-12-24 07:30:15',
      details: 'Successful authentication',
      linkTo: null, // Read-only
      readOnly: true
    },
    {
      id: 2,
      type: 'settings',
      action: 'Settings updated',
      user: 'admin@consistency.app',
      timestamp: '2025-12-24 06:15:30',
      details: 'App profile configuration changed',
      linkTo: '/app/admin/app-profile', // Deep link to App Profile
      readOnly: false
    },
    {
      id: 3,
      type: 'admin',
      action: 'Admin access',
      user: 'admin@consistency.app',
      timestamp: '2025-12-24 05:45:22',
      details: 'Accessed admin dashboard',
      linkTo: null, // Read-only
      readOnly: true
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'user':
        return <User className="h-4 w-4 text-blue-500" />;
      case 'settings':
        return <Settings className="h-4 w-4 text-purple-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-pink-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      user: 'border-blue-500/30 text-blue-500',
      settings: 'border-purple-500/30 text-purple-500',
      admin: 'border-pink-500/30 text-pink-500'
    };
    return colors[type] || 'border-gray-500/30 text-gray-500';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <FileText className="h-8 w-8 text-pink-500" />
          Activity Log
        </h1>
        <p className="text-[#8A93A6]">System event history and audit trail (Deep-link to relevant Admin pages or read-only)</p>
      </div>

      {/* Filters */}
      <Card className="glass mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#8A93A6]" />
              <Input
                placeholder="Search activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1C1F26] border-white/10 text-white"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-[#1C1F26] border-white/10 text-white">
                <Filter className="h-4 w-4 mr-2 text-[#8A93A6]" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="user">User Events</SelectItem>
                <SelectItem value="settings">Settings Changes</SelectItem>
                <SelectItem value="admin">Admin Access</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activity Feed */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-pink-500" />
            Recent Activity
          </CardTitle>
          <CardDescription>System events from the last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activityLogs.map((log) => {
              const content = (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center mt-1">
                        {getTypeIcon(log.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white font-medium">{log.action}</p>
                          <Badge variant="outline" className={getTypeBadge(log.type)}>
                            {log.type}
                          </Badge>
                          {log.readOnly && (
                            <Badge variant="outline" className="border-gray-500/30 text-gray-400">
                              Read-only
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-[#8A93A6] mb-1">{log.details}</p>
                        <div className="flex items-center gap-4 text-xs text-[#8A93A6]">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {log.user}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {log.timestamp}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );

              // Wrap in Link if there's a linkTo, otherwise render as-is
              return log.linkTo ? (
                <Link key={log.id} to={log.linkTo}>
                  {content}
                </Link>
              ) : (
                <div key={log.id}>
                  {content}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
